import { useState } from 'react';
import { facilityStaffNpi, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, btn, sx, useGuarded } from './kit';

const INPUT = 'box-sizing:border-box;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;';

/**
 * A staff member's own NPI, set by their Facility Admin (PUT /facilities/me/staff/:id/npi).
 * A practitioner on staff (an NP, say) needs it to sign the claims attested under that NPI;
 * the server checks the number and that nobody else in the building holds it.
 */
export function NpiEditor({ userId, name, npi, onSaved }: { userId: string; name: string; npi: string | null; onSaved: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [val, setVal] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const ok = /^\d{10}$/.test(val.trim());
  const save = async (next: string | null) => {
    setErr(null);
    try {
      await facilityStaffNpi(client, { params: { id: 'me', userId }, body: { npi: next } });
      setVal('');
      onSaved();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  return (
    <div data-testid="npi-editor" style={sx('margin:0 0 14px;padding-bottom:12px;border-bottom:1px solid rgba(34,39,46,.1);')}>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;')}>Practitioner NPI</div>
      <div style={sx('font-size:16px;margin-bottom:8px;')}>{I(npi ? 'NPI ' + npi + ' · signs claims attested under it' : 'None · only a practitioner who signs claims needs one')}</div>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;')}>
        <input type="text" inputMode="numeric" aria-label={`NPI for ${name}`} placeholder="10 digits" value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} style={sx(INPUT + 'width:130px;')} />
        <a href="#" {...wr(() => { if (ok) void save(val.trim()); })} aria-disabled={!ok || undefined} style={sx(btn(ok ? 'primary' : 'off'))}>
          Save NPI
        </a>
        {npi && (
          <a href="#" {...wr(() => void save(null))} style={sx(btn('ghost'))}>
            Remove
          </a>
        )}
      </div>
      <ErrLine error={err} />
    </div>
  );
}
